import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus } from 'lucide-react';

export const NewProposalDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  clients,
  companies,
  categories
}) => {
  const handleClientChange = (value) => {
    if (value === '__none__') {
      setFormData({ ...formData, client_id: '', client_name: '', company_id: '' });
      return;
    }
    const client = clients.find((c) => c.client_id === value);
    if (client) {
      setFormData({
        ...formData,
        client_id: client.client_id,
        client_name: client.name,
        company_id: client.company_id || ''
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="create-proposal-button">
          <Plus className="mr-2 h-4 w-4" />
          New Proposal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Proposal</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Proposal Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter proposal title"
              required
              data-testid="proposal-title-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Client Contact *</label>
            <Select value={formData.client_id || '__none__'} onValueChange={handleClientChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select client contact" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- Select Contact --</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.client_id} value={client.client_id}>
                    {client.name} {client.company_name && `(${client.company_name})`} {client.position && `- ${client.position}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Company {formData.company_id && <span className="text-xs text-muted-foreground">(auto-filled from contact)</span>}
            </label>
            <Select
              value={formData.company_id || '__none__'}
              onValueChange={(value) => setFormData({ ...formData, company_id: value === '__none__' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- No Company --</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.company_id} value={company.company_id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Category</label>
            <Select
              value={formData.category || '__none__'}
              onValueChange={(value) => setFormData({ ...formData, category: value === '__none__' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- Select Category --</SelectItem>
                {categories.length === 0 ? (
                  <SelectItem value="__disabled__" disabled>No categories - Add in Settings</SelectItem>
                ) : (
                  categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Requirement</label>
            <Textarea
              value={formData.requirement}
              onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
              placeholder="What does the client need?"
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Total Area of the Project - in sq.ft.</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.total_area_sqmt}
              onChange={(e) => setFormData({
                ...formData,
                total_area_sqmt: e.target.value,
                scope_area: e.target.value ? `${e.target.value} sq.ft.` : ''
              })}
              placeholder="Enter area in sq.ft."
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Description *</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Proposal description"
              required
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Amount (INR)</label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="submit-proposal-button">
              Create Proposal
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
